import { supabase } from '../lib/supabase';
import { ok, fail } from '../utils/serviceHelpers';

const BUCKET = 'branding-assets';

const DEFAULT_PALETTE = {
  primary: '#4B7A8F',
  secondary: '#9AA481',
  accent: '#CDC6FB',
};

const LEGACY_PALETTE = {
  primary: '#3B82F6',
  secondary: '#10B981',
};

const normalizeColor = value =>
  typeof value === 'string' && /^#[0-9A-F]{6}$/i.test(value.trim())
    ? value.trim().toUpperCase()
    : null;

function normalizeBrandingBundle(bundle = {}) {
  const primary = normalizeColor(bundle.primary_color);
  const secondary = normalizeColor(bundle.secondary_color);
  const accent = normalizeColor(bundle.accent_color);
  const isLegacyDefault =
    primary === LEGACY_PALETTE.primary &&
    secondary === LEGACY_PALETTE.secondary &&
    !accent;

  return {
    ...bundle,
    primary_color: isLegacyDefault
      ? DEFAULT_PALETTE.primary
      : primary || DEFAULT_PALETTE.primary,
    secondary_color: isLegacyDefault
      ? DEFAULT_PALETTE.secondary
      : secondary || DEFAULT_PALETTE.secondary,
    accent_color: accent || DEFAULT_PALETTE.accent,
  };
}
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
  'image/gif',
  'image/x-icon',
  'image/vnd.microsoft.icon',
]);

function fallbackBundle(brand, config) {
  return normalizeBrandingBundle({
    ...(brand || {}),
    brand_name:
      brand?.brand_name ||
      brand?.app_name ||
      config?.nombre_empresa ||
      'Nova Asistencia',
    primary_color:
      brand?.primary_color || config?.color_primario || DEFAULT_PALETTE.primary,
    secondary_color:
      brand?.secondary_color ||
      config?.color_secundario ||
      DEFAULT_PALETTE.secondary,
    accent_color: brand?.accent_color || DEFAULT_PALETTE.accent,
    logo_url: brand?.logo_url || config?.logo_url || null,
    moneda: config?.moneda || 'MXN',
    simbolo_moneda: config?.simbolo_moneda || '$',
    mensaje_bienvenida:
      config?.mensaje_bienvenida ||
      brand?.app_description ||
      'Sistema de gestión de asistencia y recursos humanos',
  });
}

async function getLegacyFallback() {
  const [brandResult, configResult] = await Promise.all([
    supabase
      .from('branding_settings')
      .select('*')
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('configuracion_aplicacion')
      .select(
        'id,nombre_empresa,color_primario,color_secundario,logo_url,mensaje_bienvenida,moneda,simbolo_moneda,updated_at'
      )
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  if (brandResult.error && configResult.error) {
    throw brandResult.error;
  }

  return fallbackBundle(brandResult.data, configResult.data);
}

function validateAsset(file) {
  if (!file) throw new Error('Selecciona un archivo.');
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('El archivo excede el límite de 10 MB.');
  }
  if (file.type && !ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error('Formato de imagen no permitido.');
  }
}

function safeExtension(file) {
  const name = String(file?.name || 'asset');
  const extension = name.includes('.')
    ? name.split('.').pop().toLowerCase()
    : 'bin';
  return extension.replace(/[^a-z0-9]/g, '').slice(0, 8) || 'bin';
}

async function uploadAsset(file, assetType) {
  validateAsset(file);

  const { data: { user } = {} } = await supabase.auth.getUser();
  if (!user) throw new Error('Debes iniciar sesión para subir assets.');

  const objectName = `${assetType}/${user.id}/${Date.now()}-${crypto.randomUUID()}.${safeExtension(file)}`;
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(objectName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(objectName);
  return {
    path: objectName,
    publicUrl: data?.publicUrl || null,
  };
}

export const brandingService = {
  async getBranding() {
    try {
      const { data, error } = await supabase.rpc('get_public_branding_bundle');
      if (!error && data) return ok(normalizeBrandingBundle(data));

      // Compatibility path for environments where the Enterprise migration
      // has not been applied yet (e.g. visual preview against production DB).
      return ok(await getLegacyFallback());
    } catch (error) {
      return fail(error);
    }
  },

  async getPublicBrandingSettings() {
    const result = await this.getBranding();
    if (!result?.ok)
      throw new Error(result?.error?.message || String(result?.error));

    const data = result.data || {};
    return {
      nombre_empresa:
        data.brand_name ||
        data.app_name ||
        data.company_name ||
        'Nova Asistencia',
      logo_url: data.logo_url || null,
      favicon_url: data.favicon_url || null,
      hero_image_url: data.hero_image_url || null,
      color_primario: data.primary_color || DEFAULT_PALETTE.primary,
      color_secundario: data.secondary_color || DEFAULT_PALETTE.secondary,
      color_acento: data.accent_color || DEFAULT_PALETTE.accent,
      mensaje_bienvenida:
        data.mensaje_bienvenida ||
        data.app_description ||
        'Sistema de gestión de asistencia y recursos humanos',
      moneda: data.moneda || 'MXN',
      simbolo_moneda: data.simbolo_moneda || '$',
      footer_text: data.footer_text || null,
      copyright_text: data.copyright_text || null,
    };
  },

  async upsertBranding(payload = {}) {
    try {
      const branding = {
        brand_name: payload.brand_name || payload.nombre_empresa || null,
        app_name:
          payload.app_name ||
          payload.brand_name ||
          payload.nombre_empresa ||
          null,
        app_description:
          payload.app_description || payload.mensaje_bienvenida || null,
        company_name:
          payload.company_name ||
          payload.brand_name ||
          payload.nombre_empresa ||
          null,
        primary_color:
          payload.primary_color ||
          payload.color_primario ||
          DEFAULT_PALETTE.primary,
        secondary_color:
          payload.secondary_color ||
          payload.color_secundario ||
          DEFAULT_PALETTE.secondary,
        accent_color:
          payload.accent_color ||
          payload.color_acento ||
          DEFAULT_PALETTE.accent,
        logo_url: payload.logo_url || null,
        logo_small_url: payload.logo_small_url || null,
        favicon_url: payload.favicon_url || null,
        hero_image_url: payload.hero_image_url || null,
      };
      const configuration = {
        nombre_empresa: payload.brand_name || payload.nombre_empresa || null,
        mensaje_bienvenida:
          payload.mensaje_bienvenida || payload.app_description || null,
        moneda: payload.moneda || 'MXN',
        simbolo_moneda: payload.simbolo_moneda || '$',
      };

      const { data, error } = await supabase.rpc('save_branding_bundle', {
        p_branding: branding,
        p_configuration: configuration,
      });

      if (error) return fail(error);
      return ok(normalizeBrandingBundle(data || {}));
    } catch (error) {
      return fail(error);
    }
  },

  async uploadLogo(file) {
    try {
      return ok(await uploadAsset(file, 'logo'));
    } catch (error) {
      return fail(error);
    }
  },

  async uploadFavicon(file) {
    try {
      return ok(await uploadAsset(file, 'favicon'));
    } catch (error) {
      return fail(error);
    }
  },

  async uploadHeroImage(file) {
    try {
      return ok(await uploadAsset(file, 'hero'));
    } catch (error) {
      return fail(error);
    }
  },

  applyBrandingSettings(settings = {}) {
    const primary = settings.color_primario || settings.primary_color;
    const secondary = settings.color_secundario || settings.secondary_color;
    const accent = settings.color_acento || settings.accent_color;
    if (primary) {
      document.documentElement.style.setProperty('--color-primary', primary);
      document.documentElement.style.setProperty('--brand-primary', primary);
    }
    if (secondary) {
      document.documentElement.style.setProperty(
        '--color-secondary',
        secondary
      );
      document.documentElement.style.setProperty(
        '--brand-secondary',
        secondary
      );
    }
    if (accent) {
      document.documentElement.style.setProperty('--color-accent', accent);
      document.documentElement.style.setProperty('--brand-accent', accent);
    }

    const favicon = settings.favicon_url;
    if (favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = favicon;
    }

    const name = settings.nombre_empresa || settings.brand_name;
    if (name) document.title = name;
  },

  formatCurrency(amount, currency = {}) {
    const code = currency.code || 'MXN';
    const numeric = Number(amount) || 0;

    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: code,
      }).format(numeric);
    } catch {
      return `${currency.symbol || '$'}${numeric.toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  },
};

/**
 * Backwards-compatible named exports. The Enterprise refactor consolidated
 * implementation behind brandingService, while several production modules
 * still consume the historical named API. Keep this facade until those
 * callers are migrated deliberately.
 */
export async function getBranding() {
  return brandingService.getBranding();
}

export async function getPublicBrandingSettings() {
  return brandingService.getPublicBrandingSettings();
}

export async function upsertBranding(payload = {}) {
  return brandingService.upsertBranding(payload);
}

export async function uploadLogo(file) {
  return brandingService.uploadLogo(file);
}

export async function uploadFavicon(file) {
  return brandingService.uploadFavicon(file);
}

export async function uploadHeroImage(file) {
  return brandingService.uploadHeroImage(file);
}

export function applyBrandingSettings(settings = {}) {
  return brandingService.applyBrandingSettings(settings);
}

export function formatCurrency(amount, currency = {}) {
  return brandingService.formatCurrency(amount, currency);
}

export default brandingService;
